<script>
import AppPagination from '@/components/common/AppPagination.vue'
import { createB2bCompanyProduct, deleteB2bCompanyProduct, getB2bCompany, getB2bCompanyProducts, updateB2bCompanyProduct } from '@/api/b2b.js'
import { getProductList } from '@/api/product.js'

export default {
  name: 'B2bCompanyDetailPage',
  components: { AppPagination },
  data() {
    return {
      company: null,
      items: [],
      products: [],
      productKeyword: '',
      productSearchTimer: null,
      loading: false,
      productLoading: false,
      saving: false,
      dialog: false,
      editTarget: null,
      page: 1,
      size: 10,
      total: 0,
      search: { keyword: '', status: '' },
      form: this.emptyForm(),
      displayOptions: [
        { title: '전체', value: '' },
        { title: '노출', value: 'Y' },
        { title: '숨김', value: 'N' },
      ],
      saleStatusOptions: [
        { title: '판매중', value: 'ACTIVE' },
        { title: '중지', value: 'PAUSED' },
        { title: '종료', value: 'ENDED' },
      ],
      paymentOptions: [
        { title: '카드결제', value: 'CARD' },
        { title: '가상계좌', value: 'VIRTUAL_ACCOUNT' },
      ],
    }
  },
  computed: {
    companyCode() {
      return this.$route.params.companyCode
    },
    isAllB2b() {
      return this.companyCode === 'all'
    },
    pageTitle() {
      return this.isAllB2b ? '전체 B2B 상품' : `${this.company?.companyName || '업체'} 특가상품`
    },
    productOptions() {
      return this.products.map(product => ({
        title: `${product.productName} (${product.categoryName || '-'}/${product.brandName || '-'})`,
        value: product.productCode,
      }))
    },
  },
  mounted() {
    this.fetchCompany()
    this.fetchProducts()
    this.fetchList()
  },
  methods: {
    emptyForm() {
      return {
        productCode: '',
        salePrice: 0,
        allocatedStockQty: 0,
        limitPerMember: null,
        saleStartDt: '',
        saleEndDt: '',
        displayYn: 'N',
        saleStatus: 'ACTIVE',
        paymentTypes: ['CARD', 'VIRTUAL_ACCOUNT'],
      }
    },
    async fetchCompany() {
      if (this.isAllB2b) return
      const res = await getB2bCompany(this.companyCode)
      this.company = res.data?.data || null
    },
    async fetchProducts(keyword = '') {
      this.productLoading = true
      try {
        const res = await getProductList({ page: 1, size: 50, keyword: keyword || undefined })
        this.products = res.data?.data?.items || []
      } finally {
        this.productLoading = false
      }
    },
    searchProducts(keyword) {
      this.productKeyword = keyword || ''
      window.clearTimeout(this.productSearchTimer)
      this.productSearchTimer = window.setTimeout(() => this.fetchProducts(this.productKeyword), 250)
    },
    async fetchList() {
      this.loading = true
      try {
        const res = await getB2bCompanyProducts(this.companyCode, {
          keyword: this.search.keyword || undefined,
          status: this.search.status || undefined,
          page: this.page,
          size: this.size,
        })
        this.items = res.data?.data?.items || []
        this.total = res.data?.data?.total || 0
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.editTarget = null
      this.form = this.emptyForm()
      this.dialog = true
    },
    openEdit(item) {
      this.editTarget = item
      this.form = {
        productCode: item.productCode,
        salePrice: Number(item.salePrice || 0),
        allocatedStockQty: Number(item.allocatedStockQty || 0),
        limitPerMember: item.limitPerMember,
        saleStartDt: this.toInputDateTime(item.saleStartDt),
        saleEndDt: this.toInputDateTime(item.saleEndDt),
        displayYn: item.displayYn || 'N',
        saleStatus: item.saleStatus || 'ACTIVE',
        paymentTypes: item.paymentTypes?.length ? item.paymentTypes : ['CARD', 'VIRTUAL_ACCOUNT'],
      }
      this.dialog = true
    },
    async save() {
      if (!this.form.productCode || !this.form.salePrice) return
      this.saving = true
      try {
        const payload = {
          ...this.form,
          saleStartDt: this.toApiDateTime(this.form.saleStartDt),
          saleEndDt: this.toApiDateTime(this.form.saleEndDt),
        }
        const res = this.editTarget
          ? await updateB2bCompanyProduct(this.editTarget.b2bCompanyProductCode, payload)
          : await createB2bCompanyProduct(this.companyCode, payload)
        if (res.data?.code === 0) {
          this.dialog = false
          this.fetchList()
        }
      } finally {
        this.saving = false
      }
    },
    async remove(item) {
      if (!window.confirm(`${item.productName} 특가상품을 삭제하시겠습니까?`)) return
      const res = await deleteB2bCompanyProduct(item.b2bCompanyProductCode)
      if (res.data?.code === 0) this.fetchList()
    },
    toInputDateTime(value) {
      return value ? String(value).slice(0, 16) : ''
    },
    toApiDateTime(value) {
      return value ? `${value}:00` : null
    },
    formatMoney(value) {
      return `${Number(value || 0).toLocaleString()}원`
    },
    formatDate(value) {
      return value ? String(value).replace('T', ' ').slice(0, 16) : '-'
    },
    paymentText(types) {
      const map = { CARD: '카드결제', VIRTUAL_ACCOUNT: '가상계좌' }
      return (types || []).map(type => map[type] || type).join(', ') || '-'
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-5">
      <div>
        <div class="text-h5 font-weight-bold">{{ pageTitle }}</div>
        <div class="text-body-2 text-grey mt-1">상품 마스터에서 선택해 업체별 판매가, 배정재고, 결제수단을 설정합니다.</div>
      </div>
      <div class="d-flex ga-2">
        <v-btn variant="outlined" prepend-icon="mdi-arrow-left" @click="$router.push('/b2b/companies')">업체 목록</v-btn>
        <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreate">특가상품 등록</v-btn>
      </div>
    </div>

    <v-card v-if="company" rounded="lg" class="mb-5">
      <v-card-text class="d-flex flex-wrap ga-6">
        <div><div class="text-caption text-grey">업체명</div><div class="font-weight-bold">{{ company.companyName }}</div></div>
        <div><div class="text-caption text-grey">사업자번호</div><div>{{ company.businessNo || '-' }}</div></div>
        <div><div class="text-caption text-grey">인증방식</div><div>{{ company.authMethod }}</div></div>
        <div><div class="text-caption text-grey">상태</div><div>{{ company.status }}</div></div>
      </v-card-text>
    </v-card>

    <v-card rounded="lg" class="mb-5">
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" md="6"><v-text-field v-model="search.keyword" label="상품 검색" variant="outlined" density="compact" hide-details clearable @keyup.enter="fetchList" /></v-col>
          <v-col cols="12" md="3"><v-select v-model="search.status" :items="displayOptions" label="노출여부" variant="outlined" density="compact" hide-details /></v-col>
          <v-col cols="12" md="3" class="d-flex justify-end"><v-btn color="primary" prepend-icon="mdi-magnify" @click="fetchList">검색</v-btn></v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card rounded="lg">
      <v-card-title class="text-subtitle-1 font-weight-bold">특가상품 목록 <v-chip size="small" color="primary" label class="ml-2">{{ total }}건</v-chip></v-card-title>
      <v-progress-linear v-if="loading" indeterminate color="primary" />
      <v-table density="compact">
        <thead><tr><th>상품</th><th>카테고리</th><th>브랜드</th><th>판매가</th><th>배정/판매/가용</th><th>결제수단</th><th>판매기간</th><th>노출</th><th class="text-end">관리</th></tr></thead>
        <tbody>
          <tr v-for="item in items" :key="item.b2bCompanyProductCode">
            <td class="font-weight-bold">{{ item.productName }}</td>
            <td>{{ item.categoryName || '-' }}</td>
            <td>{{ item.brandName || '-' }}</td>
            <td>{{ formatMoney(item.salePrice) }}</td>
            <td>{{ item.allocatedStockQty }} / {{ item.soldStockQty }} / {{ item.availableStockQty }}</td>
            <td>{{ paymentText(item.paymentTypes) }}</td>
            <td>{{ formatDate(item.saleStartDt) }} ~ {{ formatDate(item.saleEndDt) }}</td>
            <td><v-chip size="small" :color="item.displayYn === 'Y' ? 'primary' : 'grey'">{{ item.displayYn }}</v-chip></td>
            <td class="text-end">
              <v-btn icon="mdi-pencil" size="small" variant="text" @click="openEdit(item)" />
              <v-btn icon="mdi-delete-outline" size="small" variant="text" color="error" @click="remove(item)" />
            </td>
          </tr>
          <tr v-if="!loading && !items.length"><td colspan="9" class="text-center text-grey py-8">등록된 B2B 특가상품이 없습니다.</td></tr>
        </tbody>
      </v-table>
      <div class="px-4 pb-4"><AppPagination :total="total" :page="page" :size="size" @update:page="p => { page = p; fetchList() }" @update:size="s => { size = s; page = 1; fetchList() }" /></div>
    </v-card>

    <v-dialog v-model="dialog" max-width="760">
      <v-card rounded="lg">
        <v-card-title>{{ editTarget ? '특가상품 수정' : '특가상품 등록' }}</v-card-title>
        <v-card-text>
          <v-autocomplete
            v-model="form.productCode"
            v-model:search="productKeyword"
            :items="productOptions"
            :loading="productLoading"
            label="상품 선택 *"
            variant="outlined"
            clearable
            @update:search="searchProducts"
          />
          <v-row>
            <v-col cols="12" md="6"><v-text-field v-model.number="form.salePrice" type="number" label="B2B 판매가 *" suffix="원" variant="outlined" /></v-col>
            <v-col cols="12" md="6"><v-text-field v-model.number="form.allocatedStockQty" type="number" label="배정 재고수량" variant="outlined" /></v-col>
            <v-col cols="12" md="6"><v-text-field v-model.number="form.limitPerMember" type="number" label="회원별 구매제한 수량" variant="outlined" /></v-col>
            <v-col cols="12" md="6"><v-select v-model="form.displayYn" :items="[{ title: '노출', value: 'Y' }, { title: '숨김', value: 'N' }]" label="노출여부" variant="outlined" /></v-col>
            <v-col cols="12" md="6"><v-text-field v-model="form.saleStartDt" type="datetime-local" label="판매시작" variant="outlined" /></v-col>
            <v-col cols="12" md="6"><v-text-field v-model="form.saleEndDt" type="datetime-local" label="판매종료" variant="outlined" /></v-col>
            <v-col cols="12" md="6"><v-select v-model="form.saleStatus" :items="saleStatusOptions" label="판매상태" variant="outlined" /></v-col>
            <v-col cols="12" md="6"><v-select v-model="form.paymentTypes" :items="paymentOptions" multiple chips label="결제수단" variant="outlined" /></v-col>
          </v-row>
        </v-card-text>
        <v-card-actions><v-spacer /><v-btn variant="text" @click="dialog = false">취소</v-btn><v-btn color="primary" :loading="saving" @click="save">저장</v-btn></v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
